// Base Integration Abstract Class
// Provides common functionality for all integration types

import {
  IntegrationConfig,
  IntegrationStatus,
  ConnectionResult,
  HealthStatus,
  APIRequest,
  APIResponse,
  RetryPolicy,
  IntegrationEvent,
  TestResult,
  ValidationResult
} from '../../../types/integration';

export abstract class BaseIntegration {
  protected config: IntegrationConfig;
  protected status: IntegrationStatus = 'inactive';
  protected lastHealthCheck?: Date;
  protected eventHandlers: Map<string, Function[]> = new Map();

  constructor(config: IntegrationConfig) {
    this.config = config;
  }

  // Abstract methods that must be implemented by subclasses
  abstract connect(): Promise<ConnectionResult>;
  abstract disconnect(): Promise<void>;
  abstract testConnection(): Promise<TestResult>;
  abstract validateConfig(): Promise<ValidationResult>;

  // Common functionality for all integrations
  
  /**
   * Get current integration configuration
   */
  getConfig(): IntegrationConfig {
    return { ...this.config };
  }

  /**
   * Update integration configuration
   */
  async updateConfig(updates: Partial<IntegrationConfig>): Promise<void> {
    this.config = {
      ...this.config,
      ...updates,
      updatedAt: new Date()
    };
    
    // Emit configuration update event
    await this.emitEvent('config.updated', { updates });
  }

  /**
   * Get current integration status
   */
  getStatus(): IntegrationStatus {
    return this.status;
  }

  /**
   * Set integration status
   */
  protected async setStatus(status: IntegrationStatus): Promise<void> {
    const previousStatus = this.status;
    this.status = status;
    
    // Update config status
    this.config.status = status;
    this.config.updatedAt = new Date();
    
    // Emit status change event
    await this.emitEvent('status.changed', {
      previousStatus,
      currentStatus: status
    });
  }

  /**
   * Perform health check
   */
  async performHealthCheck(): Promise<HealthStatus> {
    const startTime = Date.now();
    
    try {
      const testResult = await this.testConnection();
      const responseTime = Date.now() - startTime;
      
      const healthStatus: HealthStatus = {
        status: testResult.success ? 'healthy' : 'unhealthy',
        lastCheck: new Date(),
        responseTime,
        errorRate: 0, // Would be calculated from recent operations
        uptime: this.calculateUptime(),
        issues: testResult.success ? [] : [{
          type: 'error',
          message: testResult.message,
          timestamp: new Date(),
          resolved: false
        }]
      };

      this.config.healthStatus = healthStatus;
      this.lastHealthCheck = new Date();
      
      return healthStatus;
    } catch (error) {
      const healthStatus: HealthStatus = {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: Date.now() - startTime,
        errorRate: 1,
        uptime: this.calculateUptime(),
        issues: [{
          type: 'error',
          message: error instanceof Error ? error.message : 'Unknown error',
          timestamp: new Date(),
          resolved: false
        }]
      };

      this.config.healthStatus = healthStatus;
      return healthStatus;
    }
  }

  /**
   * Make authenticated API request
   */
  protected async makeAPIRequest<T = any>(request: APIRequest): Promise<APIResponse<T>> {
    const startTime = Date.now();
    
    try {
      // Add authentication headers
      const authenticatedRequest = await this.addAuthHeaders(request);
      
      // Apply rate limiting
      await this.checkRateLimit();
      
      // Make the actual request (implementation would use fetch or axios)
      const response = await this.executeRequest<T>(authenticatedRequest);
      
      // Log successful request
      await this.logAPIRequest(request, response, Date.now() - startTime);
      
      return response;
    } catch (error) {
      const errorResponse: APIResponse<T> = {
        success: false,
        error: {
          code: 'REQUEST_FAILED',
          message: error instanceof Error ? error.message : 'Unknown error',
          retryable: this.isRetryableError(error)
        },
        statusCode: 0,
        headers: {},
        responseTime: Date.now() - startTime
      };
      
      // Log failed request
      await this.logAPIRequest(request, errorResponse, Date.now() - startTime);
      
      return errorResponse;
    }
  }

  /**
   * Execute request with retry logic
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    retryPolicy?: RetryPolicy
  ): Promise<T> {
    const policy = retryPolicy || this.getDefaultRetryPolicy();
    let lastError: Error;
    
    for (let attempt = 1; attempt <= policy.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        // Check if error is retryable
        if (!this.isRetryableError(error) || attempt === policy.maxAttempts) {
          throw lastError;
        }
        
        // Calculate delay for next attempt
        const delay = this.calculateRetryDelay(attempt, policy);
        await this.sleep(delay);
        
        // Emit retry event
        await this.emitEvent('operation.retry', {
          attempt,
          maxAttempts: policy.maxAttempts,
          error: lastError.message,
          nextDelay: delay
        });
      }
    }
    
    throw lastError!;
  }

  /**
   * Event system for integration lifecycle
   */
  on(eventType: string, handler: Function): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  off(eventType: string, handler: Function): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  protected async emitEvent(eventType: string, data: any): Promise<void> {
    const event: IntegrationEvent = {
      id: this.generateId(),
      type: eventType,
      integrationId: this.config.id,
      timestamp: new Date(),
      data,
      metadata: {
        platform: this.config.platform,
        type: this.config.type
      }
    };

    const handlers = this.eventHandlers.get(eventType) || [];
    
    // Execute all handlers concurrently
    await Promise.all(handlers.map(handler => {
      try {
        return handler(event);
      } catch (error) {
        console.error(`Error in event handler for ${eventType}:`, error);
      }
    }));
  }

  // Protected utility methods

  protected async addAuthHeaders(request: APIRequest): Promise<APIRequest> {
    // Implementation would decrypt credentials and add appropriate auth headers
    // This is a placeholder - actual implementation would handle OAuth, API keys, etc.
    return {
      ...request,
      headers: {
        ...request.headers,
        'Authorization': 'Bearer [token]', // Placeholder
        'User-Agent': 'EmailPlatform-Integration/1.0'
      }
    };
  }

  protected async checkRateLimit(): Promise<void> {
    // Implementation would check rate limits and throttle requests
    // This is a placeholder for actual rate limiting logic
  }

  protected async executeRequest<T>(_request: APIRequest): Promise<APIResponse<T>> {
    // Placeholder implementation - would use fetch or axios
    // This is where the actual HTTP request would be made
    return {
      success: true,
      data: {} as T,
      statusCode: 200,
      headers: {},
      responseTime: 100
    };
  }

  protected isRetryableError(error: any): boolean {
    // Determine if an error is retryable based on error type/code
    if (error?.code === 'NETWORK_ERROR') return true;
    if (error?.code === 'TIMEOUT') return true;
    if (error?.code === 'RATE_LIMIT') return true;
    if (error?.statusCode >= 500) return true;
    
    return false;
  }

  protected getDefaultRetryPolicy(): RetryPolicy {
    return {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2,
      retryableErrors: ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMIT']
    };
  }

  protected calculateRetryDelay(attempt: number, policy: RetryPolicy): number {
    const delay = policy.baseDelay * Math.pow(policy.backoffMultiplier, attempt - 1);
    return Math.min(delay, policy.maxDelay);
  }

  protected async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  protected calculateUptime(): number {
    // Calculate uptime percentage - placeholder implementation
    return 99.9;
  }

  protected async logAPIRequest(
    request: APIRequest,
    response: APIResponse,
    duration: number
  ): Promise<void> {
    // Log API request for monitoring and debugging
    console.log(`API Request: ${request.method} ${request.url} - ${response.statusCode} (${duration}ms)`);
  }

  protected generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Lifecycle methods

  async initialize(): Promise<void> {
    await this.setStatus('configuring');
    
    try {
      // Validate configuration
      const validation = await this.validateConfig();
      if (!validation.valid) {
        throw new Error(`Configuration validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
      }
      
      // Attempt connection
      const connectionResult = await this.connect();
      if (!connectionResult.success) {
        throw new Error(`Connection failed: ${connectionResult.message}`);
      }
      
      await this.setStatus('active');
      await this.emitEvent('integration.initialized', { connectionResult });
      
    } catch (error) {
      await this.setStatus('error');
      await this.emitEvent('integration.error', { error: error instanceof Error ? error.message : 'Unknown error' });
      throw error;
    }
  }

  async destroy(): Promise<void> {
    try {
      await this.disconnect();
      await this.setStatus('inactive');
      await this.emitEvent('integration.destroyed', {});
      
      // Clear event handlers
      this.eventHandlers.clear();
      
    } catch (error) {
      console.error('Error during integration destruction:', error);
      throw error;
    }
  }
}